import { ReactElement, ReactNode } from 'react';
import { APIPropTypes, PropTypes } from 'typed/imports';
import { ComponentEventHandler, ComponentEventHandler1, ConnectedProps, PureComponent, ReactEventHandler, connect } from 'typed/components/utils';
import { ProductPackage, RequestStatus, UserRoleBillingUserType, entities } from 'typed/api';
import { DBBoolean, MAX_SMART_STRATEGY_PACKAGE_USER_COUNT } from 'utils/constants';
import * as untypedActions from 'actions';
import FormattedResource, { Resource, isResource, resourcePropType } from 'typed/components/common/FormattedResource/FormattedResource';
import UserSelectionList, { TableColumn as UserSelectionListColumn } from 'typed/components/common/UserSelectionList/UserSelectionList';
import { DropPanel, EntityEditForm, Popup, User } from 'components/common';
import { ComponentLoader } from 'components/routing';
import './UserSelect.less';

export const isUserVisible = (user: entities.UserLocal) => user.HasAccessTo === DBBoolean.TRUE;

const TABLE_COLUMNS = [
    UserSelectionListColumn.SELECTION,
    UserSelectionListColumn.USER,
    UserSelectionListColumn.ROLE,
    UserSelectionListColumn.TEAM
];

interface StoreProps {
    users: entities.NetworkArray<entities.UserLocal>;
    companyAccesses: entities.NetworkArray<entities.UserCompanyAccess>;
}

interface ClearableOwnProps {
    clearable?: true;
    onChange?: ComponentEventHandler1<number | null>;
}

interface NonClearableOwnProps {
    clearable: false;
    onChange?: ComponentEventHandler1<number>;
}

interface AdditionalButton {
    className?: string;
    name: Resource;
    opposite?: boolean;
    disabled?: boolean;
    onClick?: any;
}

type OwnProps = (ClearableOwnProps | NonClearableOwnProps) & {
    className?: string;
    placeholder?: ReactElement | Resource;
    inline?: boolean;
    value?: number | null | undefined;
    onlyNormalUsers?: boolean;
    onlyManagedUsers?: boolean;
    hiddenUserIDs?: number[];
    hideAddButton?: boolean;
    createExternalUsers?: boolean;
    additionalButtons?: (ReactNode | AdditionalButton)[];
    disabled?: boolean;
    onOpen?: ComponentEventHandler;
    onClose?: ComponentEventHandler;
};

type Props = OwnProps & StoreProps & ConnectedProps;

interface State {
    open: boolean;
    addUserPopupVisible: boolean;
    editUserPopupVisible: boolean;
}

class UserSelect extends PureComponent<Props> {
    static readonly isUserVisible = isUserVisible;

    static readonly propTypes = {
        className: PropTypes.string,
        placeholder: PropTypes.oneOfType([PropTypes.element, resourcePropType]),
        inline: PropTypes.bool,
        clearable: PropTypes.bool,
        value: PropTypes.number,
        onlyNormalUsers: PropTypes.bool,
        onlyManagedUsers: PropTypes.bool,
        hiddenUserIDs: PropTypes.arrayOf(PropTypes.number),
        hideAddButton: PropTypes.bool,
        createExternalUsers: PropTypes.bool,
        additionalButtons: PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.shape({
                className: PropTypes.string,
                name: FormattedResource.resourcePropType,
                opposite: PropTypes.bool,
                disabled: PropTypes.bool,
                onClick: PropTypes.func
            })
        ])),
        disabled: PropTypes.bool,
        onOpen: PropTypes.func,
        onClose: PropTypes.func,
        onChange: PropTypes.func,

        users: APIPropTypes.networkArrayOf(APIPropTypes.UserLocal).isRequired,
        companyAccesses: APIPropTypes.networkArrayOf(APIPropTypes.UserCompanyAccess).isRequired
    };

    static readonly defaultProps = {
        className: '',
        placeholder: '',
        clearable: true,
        inline: false,
        value: null,
        onlyNormalUsers: true,
        onlyManagedUsers: false,
        hiddenUserIDs: [],
        hideAddButton: false,
        createExternalUsers: false,
        additionalButtons: [],
        disabled: false
    };

    state: State = {
        open: false,
        addUserPopupVisible: false,
        editUserPopupVisible: false
    };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (nextProps.users.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(untypedActions.loadAllUsers());
        }

        if (nextProps.onlyNormalUsers && nextProps.companyAccesses.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(untypedActions.loadAllUserCompanyAccesses());
        }

        if (this.props.value !== nextProps.value && nextProps.value && (this._preventDropPanelClosing || this.state.open)) {
            this._preventDropPanelClosing = false;

            this.setState({ open: false });
            if (this.props.onClose) { this.props.onClose(this); }
        }
    }

    private _preventDropPanelClosing = false;


    private _onClearSelectionClick: ReactEventHandler = (event) => {
        (event as unknown as MouseEvent).stopPropagation();
        (event as unknown as MouseEvent).preventDefault();

        if (this.props.onChange) {
            this.props.onChange(this, null as unknown as number);
        }
    };


    private _onUserSelectionOpen: ComponentEventHandler = () => {
        if (this.props.onOpen) { this.props.onOpen(this); }
        this.setState({ open: true });
    };

    private _onUserSelectionClose: ComponentEventHandler = (_sender) => {
        if (this._preventDropPanelClosing) {
            this._preventDropPanelClosing = false;
        }
        else if (!this.state.addUserPopupVisible && !this.state.editUserPopupVisible) {
            this.setState({ open: false });
            if (this.props.onClose) { this.props.onClose(this); }
        }
    };


    private _onSelectedUserChange: ComponentEventHandler1<number[]> = (_sender, value) => {
        const userID = value.first() || null;

        if (this.props.onChange && (this.props.clearable || userID)) {
            this.props.onChange(this, userID!);
        }
    };

    private _onUserEditingStatusChange: ComponentEventHandler1<boolean> = (_sender, value) => {
        this.setState({ editUserPopupVisible: value });
    };


    private _onAddUserClick = () => {
        this.setState({ addUserPopupVisible: true });
    };

    private _onAddUserCancel: ComponentEventHandler = () => {
        this._preventDropPanelClosing = true;
        this.setState({ addUserPopupVisible: false });
    };

    private _onUserSaved: ComponentEventHandler1<number> = (_sender, userID) => {
        this._preventDropPanelClosing = true;
        this.setState({ addUserPopupVisible: false });

        if (this.props.onChange) {
            this.props.onChange(this, userID);
        }
    };


    private _onSelectMeClick: ReactEventHandler = () => {
        this._onSelectedUserChange(this, [this.props.session.UserID]);
    };


    private _renderForm(className = '') {
        const
            { value, hiddenUserIDs, disabled, session } = this.props,

            canUserAddNewUsers = !this.props.hideAddButton && (
                session.IsUserAdministrator || session.IsUser4SAdministrator ||
                session.UseUserRoleBasedBilling && session.UserRoleBillingUserTypeID &&
                session.UserRoleBillingUserTypeID !== UserRoleBillingUserType.PARTICIPANT
            ) && (
                session.ProductPackageID !== ProductPackage.SMART_STRATEGY ||
                this.props.users.Custom.status === RequestStatus.SUCCEEDED &&
                this.props.users.Data.count(user => user.HasAccessTo === DBBoolean.TRUE && !user.AdministrationUserTypeID) < MAX_SMART_STRATEGY_PACKAGE_USER_COUNT
            );

        return (
            <EntityEditForm
                className={className}
                title=""
                // eslint-disable-next-line react/jsx-handler-names
                additionalButtons={[
                    canUserAddNewUsers ? <button key="add" onClick={this._onAddUserClick}><FormattedResource resource={{ id: 'users.add_new_user' }} /></button> : null,
                    !hiddenUserIDs?.includes(session.UserID) && value !== session.UserID ?
                        <button key="me" onClick={this._onSelectMeClick}><FormattedResource resource={{ id: 'users.select_me' }} /></button> :
                        null,
                    ...this.props.additionalButtons!
                ].filter(v => v)}
                cancel={null}
                submit={null}
            >
                <UserSelectionList
                    fixedHeader
                    columns={TABLE_COLUMNS}
                    value={value ? [value] : []}
                    isMultiSelect={false}
                    onlyNormalUsers={this.props.onlyNormalUsers}
                    onlyManagedUsers={this.props.onlyManagedUsers}
                    hiddenUserIDs={hiddenUserIDs}
                    disabled={disabled}
                    onChange={this._onSelectedUserChange}
                    onUserEditingStatusChange={this._onUserEditingStatusChange}
                />
            </EntityEditForm>
        );
    }

    private _renderAddUserPopup() {
        if (!this.state.addUserPopupVisible) { return null; }

        return (
            <Popup className="new add-user-popup" dimmed closeMode={Popup.CloseMode.CLOSE_BUTTON} onClose={this._onAddUserCancel}>
                <ComponentLoader
                    scope="user-list"
                    component="UserManagement"
                    userID={null}
                    isExternalUser={this.props.createExternalUsers}
                    onCancel={this._onAddUserCancel}
                    onUserSaved={this._onUserSaved}
                />
            </Popup>
        );
    }

    render() {
        const
            { placeholder, value, disabled } = this.props,
            className = `user-select ${this.props.className}`.trim();

        return (
            <div className={`${className}${this.props.inline ? ' inline' : ''}`}>
                {
                    !this.props.inline ?
                        <DropPanel
                            className={className.replace(/(\S+)/g, '$1-content')}
                            title={
                                value ?
                                    <User userID={value} /> :
                                    <span className="placeholder">{isResource(placeholder) ? <FormattedResource resource={placeholder} /> : placeholder}</span>
                            }
                            // eslint-disable-next-line react/jsx-handler-names
                            icon={this.props.clearable && value && !disabled ? <button className="inline fa fa-times" onClick={this._onClearSelectionClick} /> : DropPanel.AUTO_ICON}
                            disabled={disabled}
                            opened={this.state.open}
                            onOpen={this._onUserSelectionOpen}
                            onClose={this._onUserSelectionClose}
                        >
                            {this._renderForm()}
                        </DropPanel> :
                        this._renderForm(className.replace(/(\S+)/g, '$1-content'))
                }

                {this._renderAddUserPopup()}
            </div>
        );
    }
}

export default connect<StoreProps, OwnProps>(state => ({
    users: (state.app as any).users,
    companyAccesses: (state.app as any).userCompanyAccesses
}))(UserSelect);
